import { useEffect, useState } from 'react'
import { DialogContent, DialogTitle, DialogActions } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { KDialog } from 'components/core/KDialog'
import { KDialogCloseButton } from 'components/ordering/KDialogCloseButton'
import { AddressList } from 'components/account/AddressList'
import { Address, AddressType } from 'models/Address'
import { KButton } from 'components/core/KButton'

export interface AddressSelectionDialogProps {
  open: boolean
  onClose: () => void
  addresses: Address[]
  addressType: AddressType
  onSelectAddress: (address: Address) => void
  onSave?: (data: Address) => Promise<void>
  onDelete?: (addressId?: string) => Promise<void>
  selectedAddress?: Address
}

export const AddressSelectionDialog = ({
  open,
  onClose,
  addresses,
  addressType,
  onSelectAddress,
  onSave,
  onDelete,
  selectedAddress,
}: AddressSelectionDialogProps) => {
  const { t } = useTranslation()
  const [currentSelectedAddress, setCurrentSelectedAddress] = useState<
    Address | undefined
  >(selectedAddress)

  useEffect(() => {
    setCurrentSelectedAddress(selectedAddress)
  }, [selectedAddress])

  const handleSelectAddress = (address: Address) => {
    setCurrentSelectedAddress(address)
  }

  const handleSaveChanges = () => {
    if (currentSelectedAddress) {
      onSelectAddress(currentSelectedAddress)
      onClose()
    }
  }

  const title =
    addressType === AddressType.Billing
      ? t('Select billing address')
      : t('Select delivery address')

  return (
    <KDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle variant="h4" sx={{ paddingX: 0 }}>
        {title}
      </DialogTitle>

      <DialogContent dividers sx={{ paddingX: 0 }}>
        <AddressList
          addresses={addresses}
          addressType={addressType}
          selectionMode={true}
          onSelectAddress={handleSelectAddress}
          onSave={onSave}
          onDelete={onDelete}
          selectedAddress={currentSelectedAddress}
          hideActionButtons={true}
        />
      </DialogContent>

      <DialogActions sx={{ paddingX: 0 }}>
        <KButton
          onClick={handleSaveChanges}
          disabled={!currentSelectedAddress}
          size="medium"
        >
          {t('Save changes')}
        </KButton>
      </DialogActions>

      <KDialogCloseButton onClick={onClose} />
    </KDialog>
  )
}
